"use client";

import For from "@/components/common/For";
import If from "@/components/common/If";
import { BlogCard } from "@/components/ui/Card/Card";
import { formatDate, formatLocalizedDate } from "@/lib/utils";
import { Accordion } from "@radix-ui/react-accordion";

import { Plus, X } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import Image from "@/components/common/Image";


const categories = {
  language: ["English", "French"],
};

const languageFilters = ["English", "French"];

const translations = {
  English: {
    newsRelease: "News Release",
    noResults: "No results found",
    posts: "posts",
    post: "post",
    reset: "Reset",
    search: "Search",
    filters: "Filters",
    close: "Close",
    language: "Language",
  },
  French: {
    newsRelease: "Communiqué de presse",
    noResults: "Aucun résultat trouvé",
    posts: "articles",
    post: "article",
    reset: "Réinitialiser",
    search: "Rechercher",
    filters: "Filtres",
    close: "Fermer",
    language: "Langue",
  },
};

const expandedTags = {
  category: "Category",
  market: "Market",
  solution: "Solution",
  blog: "Blog",
  caseStudy: "Case Studies",
  flushWoodVeneer: "Flush Wood Veneer",
  flushPaintable: "Flush Paintable",
  flushLaminates: "Flush Laminates",
  stileRail: "Stile & Rail",
  durability: "Durability",
  aesthetics: "Aesthetics",
  cleanability: "Cleanability",
  acoustics: "Acoustics",
  healthcare: "Healthcare",
  hospitality: "Hospitality",
  education: "Education",
  officeMultiUse: "Office / Multi-Use",
  English: "English",
  French: "French",
};

const reverseExpandedTags = Object.fromEntries(
  Object.entries(expandedTags).map(([key, value]) => [value, key])
);

export default function UI({ posts = [] }) {
  const query = useSearchParams();
  const router = useRouter();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(["English"]);
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  
  // Detect current language from filters
  const currentLanguage = filters.find((f) => languageFilters.includes(f)) || "English";
  const t = translations[currentLanguage];

  const filterResults = useCallback(
    (data, currentSearch = "", currentFilters = []) => {
      const searchValue = currentSearch?.toLowerCase();

      if (!searchValue && !currentFilters?.length) {
        // If no filters, default to English
        return data.filter((item) => item.language === "English");
      }

      let filteredData = [...data];

      if (searchValue) {
        filteredData = filteredData.filter((item) =>
          item?.title?.toLowerCase()?.includes(searchValue)
        );
      }

      if (currentFilters.length > 0) {        
        filteredData = filteredData.filter((item) => {
          const allTags = item.contentfulMetadata.tags.map((tag) => tag.name);
          
          return currentFilters.every((filter) => {
            // Check if filter matches the language property
            if (filter === item.language) {
              return true;
            }
            // Otherwise check if it's in the tags
            return allTags?.includes(filter);
          });
        });
      }

      return filteredData;
    },
    []
  );

  useEffect(() => {
    setResults(filterResults(posts, search, filters));
  }, [search, filters]);

  useEffect(() => {
    const tags = query.get("tags");
    const newFilters =
      !tags || tags === "all"
        ? ["English"]
        : tags
            .split(",")
            .map((tag) => expandedTags[tag.trim()])
            .filter(Boolean);

    setFilters(newFilters);
    setResults(filterResults(posts, search, newFilters));
  }, [query, posts]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleFilter = useCallback(
    (filter) => {
      const hasFilter = filters.includes(filter);
      const isLanguageFilter = languageFilters.includes(filter);
      
      let newFilters;
      
      if (isLanguageFilter) {
        // For language filters, only allow one at a time
        newFilters = hasFilter
          ? ["English"] // Default back to English if unchecking
          : filters.filter((item) => !languageFilters.includes(item)).concat(filter);
      } else {
        // For other filters, allow multiple
        newFilters = hasFilter
          ? filters.filter((item) => item !== filter)
          : [...filters, filter];
      }

      setFilters(newFilters);

      const queryTags =
        newFilters.length === 0
          ? "all"
          : newFilters
              .map((tag) => reverseExpandedTags[tag])
              .filter(Boolean)
              .join(",");

      const searchParams = new URLSearchParams();
      searchParams.set("tags", queryTags);
      router.push(`?${searchParams.toString()}`);
    },
    [filters, router]
  );

  const formatTitle = (title) => {
    const titleArray = title.split("_");
    const id = titleArray.splice(0, 1);
    const theRest = titleArray.join("_");
    return `${id} ${theRest?.replace(/_/g, " ")?.replace(/-/g, " ")?.replace(".pdf", "")}`;
  };
  
  // const formatLocalizedDate = (date: string, language: string) => {
  //   const dateOnly = date.split('T')[0];
  //   const [year, month, day] = dateOnly.split('-').map(Number);
  //   const d = new Date(year, month - 1, day);
    
  //   const locale = language === "French" ? "fr-FR" : "en-US";
  //   return d.toLocaleDateString(locale, {
  //     year: "numeric",
  //     month: "long",
  //     day: "numeric",
  //     timeZone: "America/New_York",
  //   });
  // };
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof ref.current?.classList === "undefined") return;

    if (window.innerWidth < 768) {
      ref.current.dataset.lenisPrevent = "true";
    } else {
      delete ref.current.dataset.lenisPrevent;
    }
  }, [isOpen]);


  console.log(results);

  return (
    <div className="resource-grid container mx-auto px-gutter mb-20">
      <button onClick={() => setIsOpen(!isOpen)} className="flex md:hidden">
        {t.filters} <ChevronRight />
      </button>
      <div className={`filters stack gap-6 ${isOpen ? "open" : ""}`} ref={ref}>
        <button onClick={() => setIsOpen(false)} className="flex md:hidden">
          <ChevronLeft />
          {t.close}
        </button>
        <header className="stack gap-2 bg-[#F2F2F2]   p-4">
          <input placeholder={t.search} onChange={handleSearch} value={search} />

          <div className="flex gap-2 flex-wrap">
            {filters.map((filter) => {
              return (
                <button
                  key={filter}
                  onClick={() => {
                    handleFilter(filter);
                  }}
                  className="border border-gold text-gold px-2 py-1 text-sm flex gap-2  items-center  "
                >
                  {filter} <X size={12} />
                </button>
              );
            })}
          </div>
          <div className="flex gap-4 justify-between">
            <If condition={filters?.length > 1 || !filters.includes("English")}>
              <button
                onClick={() => {
                  setFilters(["English"]);
                  router.push('?tags=all');
                }}
                className="flex gap-1 text-gray"
              >
                {/* <X /> */}
                {t.reset}
              </button>
            </If>

            <span className="font-bold">
              {results?.length !== 1
                ? `${results?.length} ${t.posts}`
                : `1 ${t.post}`}
            </span>
          </div>
        </header>

        <For
          each={Object.entries(categories)}
          render={([category, items]) => {
            return (
              <>
                <h2 className="font-bold uppercase">{category}</h2>
                <ul className="stack gap-4 px-4" key={category}>
                  <For
                    each={items}
                    render={(item) => (
                      <li key={item}>
                        <label className="flex gap-2 items-center leading-none">
                          <input
                            type="checkbox"
                            className="text-gold"
                            checked={filters?.includes(item)}
                            onChange={() => handleFilter(item)}
                          />
                          {item}
                        </label>
                      </li>
                    )}
                  />
                </ul>
              </>
            );
          }}
        />
      </div>

      <div>
        <If condition={!results?.length}>
          <p>{t.noResults}</p>
        </If>

        <If condition={results?.length > 0}>
          <div className="results flex flex-col gap-16">
            {results?.map((resource) => {
              return (
                <article className="flex flex-col md:flex-row items-start justify-start gap-8">
                  <Link href={`/news/${resource?.handle}`} className="w-[100px] flex-[0_0_200px]">
                    <Image
                      src={resource?.heroBackground?.url || ""}
                      alt={resource?.title || ""}
                      width={500}
                      height={500}
                      fillContainer
                      cover
                      imgClassName="aspect-[16/12] w-[200px]"
                    />
                  </Link>
                  <Link href={`/news/${resource?.handle}`} className="flex flex-col gap-2">
                    <p className="text-sm font-bold text-[#a1a1a1]">{t.newsRelease}</p>
                    <h2 className="text-2xl">{resource?.title}</h2>
                    <div className="h-2 w-10 bg-gold mb-4"></div>
                    <p className="text-sm text-[#a1a1a1]">{formatLocalizedDate(resource?.publishDate, currentLanguage)}</p>
                  </Link>
                </article>
                // <BlogCard
                //   image={
                //     resource?.heroImage?.url ||
                //     resource?.blogHeroImage?.url ||
                //     resource?.imageLink ||
                //     resource?.cardImage?.[0]?.cdn_url ||
                //     resource?.brandfolderHeroImage?.[0]?.cdn_url
                //   }
                //   title={resource?.title}
                //   date={resource?.publishDate}
                //   description={""}
                //   handle={
                //     `/whats-happening/${resource?.handle}`
                //   }
                //   isBlog={true}
                // />
              );
            })}
          </div>
        </If>
      </div>
    </div>
  );
}

const Details = ({ title, children }) => {
  const [currentOpen, setCurrentOpen] = useState(true);
  return (
    <Accordion.Root
      className="w-full"
      collapsible
      onValueChange={(value) => {
        setCurrentOpen(value);
      }}
      defaultValue={title}
    >
      <Accordion.Item
        value={title}
        className="border-b border-b-white px-4 w-full block"
      >
        <Accordion.Header>
          <Accordion.Trigger className={`stack gap-2 w-full pb-1`}>
            <div className="flex justify-between items-center w-full">
              <h2 className="font-bold uppercase">{title}</h2>
              <Plus
                className={`transtion-all ease-in-out duration-300 relative block ${
                  currentOpen ? "rotate-45" : ""
                }`}
                size={32}
                color="black"
                strokeWidth={0.5}
              />
            </div>
          </Accordion.Trigger>
        </Accordion.Header>

        <Accordion.Content
          className={`
                        overflow-hidden transition-all duration-300 ease-in-out
                        data-[state=open]:animate-slideDown
                        data-[state=closed]:animate-slideUp
                      `}
        >
          <div className="py-4">{children}</div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
